import { useContext, useEffect, useRef, useState } from 'react';
import { motion, cubicBezier, useInView, useReducedMotion } from 'motion/react';
import { useDimensions } from '../../context/ThemeContextManagement';
import { calculateDelay, REVEAL_MARGIN } from '../../utils/animations';
import { ComponentInViewContext } from '../component-wrapper/ComponentWrapper';

/**
 * Splits its content into lines and reveals them upwards line by line,
 * masked, once the surrounding ComponentWrapper is in view.
 *
 * GSAP SplitText is used for measuring the line breaks only (loaded on
 * demand, split → capture → revert) — all animation is Motion.
 *
 * Pass raw WP markup via `html`, or plain text as `children`.
 * `onSplit` receives `{ lines, label }` on every (re-)split, e.g. to read
 * `split.lines.length`.
 *
 * In-view detection observes itself by default. Pass `deferToParent` to
 * instead defer to the surrounding ComponentWrapper's in-view context (so
 * siblings reveal in sync) — if no wrapper ancestor exists, it falls back
 * to observing itself anyway. Pass `inView` to override both.
 */
export default function AnimateSplitLines({
  children,
  html,
  index = 1,
  animationOrder = 0,
  delay = 0,
  duration = 1.3,
  stagger = 0.17,
  linesClass = 'AnimateSplitLines-line',
  className = '',
  onSplit,
  inView,
  deferToParent = false,
}) {
  const sourceRef = useRef(null);
  const hasPlayed = useRef(false);
  const [split, setSplit] = useState(null); // { lines: [html], label }

  const contextInView = useContext(ComponentInViewContext);
  const selfInView = useInView(sourceRef, {
    once: true,
    margin: REVEAL_MARGIN,
  });
  const isInView =
    inView ?? (deferToParent ? (contextInView ?? selfInView) : selfInView);
  const reduceMotion = useReducedMotion();
  const { screenWidth } = useDimensions();

  const onSplitRef = useRef(onSplit);
  onSplitRef.current = onSplit;

  // Measure line breaks; re-measures when the viewport width changes.
  useEffect(() => {
    if (!sourceRef.current) return;

    let cancelled = false;

    (async () => {
      const [{ SplitText }] = await Promise.all([
        import('gsap/SplitText'),
        document.fonts.ready, // measure with the real font, not the fallback
      ]);
      if (cancelled || !sourceRef.current) return;

      const instance = new SplitText(sourceRef.current, { type: 'lines' });
      const lines = instance.lines.map((line) => line.innerHTML);
      const label = sourceRef.current.textContent;
      instance.revert();

      const result = { lines, label };
      onSplitRef.current?.(result);
      setSplit(result);
    })();

    return () => {
      cancelled = true;
    };
  }, [screenWidth, html, children]);

  // Once the reveal has *finished*, later re-splits (resize) must not replay
  // it — freshly mounted lines render directly in their final state. Set from
  // the last line's onAnimationComplete, not from `isInView`: flipping it the
  // moment the trigger fires marks the reveal as played while it is still
  // mid-flight, so a re-split during the animation snaps everything to its end
  // state instead of letting it run.
  const markPlayed = () => {
    if (isInView) hasPlayed.current = true;
  };

  if (!children && !html) return null;

  const easing = cubicBezier(0.43, 0.195, 0.02, 1);

  // Masks with an animated clip-path instead of overflow: clip — overflow
  // permanently crops any glyph ink that extends past the line's box (tall
  // ascenders, descenders), while clip-path fully relaxes once the reveal
  // finishes, so oversized fonts aren't left cropped at rest.
  const maskVariants = {
    hidden: { clipPath: 'inset(0% 0% 120% 0%)' },
    visible: (i) => ({
      clipPath: 'inset(0% 0% -10% 0%)',
      transition: {
        delay: calculateDelay(animationOrder, index, delay) + i * stagger,
        duration,
        ease: easing,
      },
    }),
  };

  const lineVariants = {
    hidden: { y: '80%', opacity: 0 },
    visible: (i) => ({
      y: '0%',
      opacity: 1,
      transition: {
        delay: calculateDelay(animationOrder, index, delay) + i * stagger,
        duration,
        ease: easing,
        opacity: { duration: duration + 0.05 },
      },
    }),
  };

  const contentProps = html
    ? { dangerouslySetInnerHTML: { __html: html } }
    : { children };

  return (
    <span
      className={className}
      style={{ display: 'block', position: 'relative' }}
      role={split ? 'text' : undefined}
      aria-label={split ? split.label : undefined}
    >
      {/* Source content: kept visually hidden (but in flow, so SplitText can
          measure it) until split, then kept in place out of flow so
          re-splits on resize can re-measure it. */}
      <span
        ref={sourceRef}
        aria-hidden={split ? true : undefined}
        style={
          split
            ? {
                display: 'block',
                position: 'absolute',
                inset: 0,
                visibility: 'hidden',
                pointerEvents: 'none',
              }
            : { display: 'block', visibility: 'hidden' }
        }
        {...contentProps}
      />

      {split && (
        <span aria-hidden="true" style={{ display: 'block' }}>
          {split.lines.map((line, i) =>
            reduceMotion ? (
              <span
                key={`${i}-${line}`}
                className={linesClass}
                style={{ display: 'block' }}
                dangerouslySetInnerHTML={{ __html: line }}
              />
            ) : (
              <motion.span
                key={`${i}-${line}`}
                className={linesClass}
                variants={maskVariants}
                custom={i}
                initial={hasPlayed.current ? false : 'hidden'}
                animate={isInView ? 'visible' : 'hidden'}
                style={{ display: 'block', willChange: 'clip-path' }}
              >
                <motion.span
                  variants={lineVariants}
                  custom={i}
                  initial={hasPlayed.current ? false : 'hidden'}
                  animate={isInView ? 'visible' : 'hidden'}
                  onAnimationComplete={
                    i === split.lines.length - 1 ? markPlayed : undefined
                  }
                  style={{ display: 'block', willChange: 'transform' }}
                  dangerouslySetInnerHTML={{ __html: line }}
                />
              </motion.span>
            )
          )}
        </span>
      )}
    </span>
  );
}
